import {
  CloseOutlined,
  DownOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import { Input, Tooltip } from 'antd'
import {
  Fragment,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import Loader from '@/components/Atoms/Loader/Loader'
import UniformSlottedCheckbox from '@/components/Filters/UniformSlottedCheckbox/UniformSlottedCheckbox'
import {
  buildSlotValue,
  formatSlottedValues,
  formatSlotValue,
  getSlotLabel,
  isUniformSlottedEligible,
  parseSlotValue,
} from '@/utils/search/characteristics/slottedCharacteristics'

import type { ActiveCharacteristicFilter } from '../../../AttributeProcessing.types'
import SlotPopover from '../SlotPopover/SlotPopover'
import cn from './SlottedFilterInput.module.scss'

const MAX_SLOT_ROWS = 5

const getSlotRowsCount = (filter: ActiveCharacteristicFilter): number => {
  const maxChosenIndex = filter.chosenOptions.reduce(
    (max, raw) => Math.max(max, parseSlotValue(raw)?.slotIndex ?? 0),
    0
  )
  return Math.min(
    MAX_SLOT_ROWS,
    Math.max(1, filter.slotRows ?? 1, maxChosenIndex)
  )
}

const getSlotValues = (
  filter: ActiveCharacteristicFilter,
  slotIndex: number
): string[] =>
  filter.chosenOptions.flatMap((raw) => {
    const parsed = parseSlotValue(raw)
    return parsed && parsed.slotIndex === slotIndex ? [parsed.value] : []
  })

const withSlotValues = (
  filter: ActiveCharacteristicFilter,
  slotIndex: number,
  values: string[]
): string[] =>
  [
    ...filter.chosenOptions.filter(
      (raw) => parseSlotValue(raw)?.slotIndex !== slotIndex
    ),
    ...values.map((value) => buildSlotValue(slotIndex, value)),
  ].sort(
    (a, b) =>
      (parseSlotValue(a)?.slotIndex ?? 0) - (parseSlotValue(b)?.slotIndex ?? 0)
  )

type SlottedFilterInputProps = {
  filter: ActiveCharacteristicFilter
  uniformChecked: boolean
  onChange: (patch: Partial<ActiveCharacteristicFilter>) => void
}

function SlottedFilterInput({
  filter,
  uniformChecked,
  onChange,
}: SlottedFilterInputProps) {
  const [openSlot, setOpenSlot] = useState<number | null>(null)
  const [search, setSearch] = useState('')
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const chipRefs = useRef<Record<number, HTMLButtonElement | null>>({})
  const rowsCount = getSlotRowsCount(filter)
  const isUniformEligible = isUniformSlottedEligible(filter.chosenOptions)
  const showUniform = isUniformEligible || !!filter.matchUniform
  const totalSummary = formatSlottedValues(filter.chosenOptions)
  const lastFilledSlot = filter.chosenOptions.reduce(
    (max, raw) => Math.max(max, parseSlotValue(raw)?.slotIndex ?? 0),
    0
  )
  const summaryGroups = Array.from({ length: lastFilledSlot }, (_, i) =>
    getSlotValues(filter, i + 1).map(formatSlotValue)
  )
  const sortedOptions = useMemo(
    () =>
      Array.from(
        new Set((filter.referenceOptions ?? []).map((option) => option.value))
      ).sort((a, b) => a.localeCompare(b, 'ru', { numeric: true })),
    [filter.referenceOptions]
  )

  const openPopover = (slotIndex: number | null) => {
    setSearch('')
    setOpenSlot(slotIndex)
  }

  const closePopover = useCallback(() => setOpenSlot(null), [])

  useLayoutEffect(() => {
    setAnchorEl(openSlot === null ? null : (chipRefs.current[openSlot] ?? null))
  }, [openSlot, rowsCount])

  const toggleValue = (slotIndex: number, value: string) => {
    const current = getSlotValues(filter, slotIndex)
    const next = current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value]
    onChange({ chosenOptions: withSlotValues(filter, slotIndex, next) })
  }

  const removeLastSlot = () => {
    openPopover(null)
    onChange({
      slotRows: rowsCount - 1,
      chosenOptions: filter.chosenOptions.filter(
        (raw) => (parseSlotValue(raw)?.slotIndex ?? 0) < rowsCount
      ),
    })
  }

  const renderPopoverContent = (slotIndex: number) => {
    const selectedValues = getSlotValues(filter, slotIndex)
    const selected = new Set(selectedValues)
    const query = search.trim().toLowerCase()
    const visibleOptions = query
      ? sortedOptions.filter((value) =>
          formatSlotValue(value).toLowerCase().includes(query)
        )
      : sortedOptions

    return (
      <div className={cn.popoverContent}>
        <div className={cn.popoverHeader}>
          <span className={cn.popoverTitle}>{getSlotLabel(slotIndex)}</span>
          <span className={cn.popoverCounter}>
            {selectedValues.length
              ? `выбрано: ${selectedValues.length}`
              : 'любое значение'}
          </span>
          <button
            type="button"
            className={cn.iconBtn}
            onClick={() => openPopover(null)}
          >
            <CloseOutlined />
          </button>
        </div>
        {selectedValues.length > 0 && (
          <div className={cn.selectedRow}>
            {selectedValues.map((value) => (
              <button
                type="button"
                key={value}
                className={cn.selectedTag}
                onClick={() => toggleValue(slotIndex, value)}
              >
                {formatSlotValue(value)}
                <CloseOutlined className={cn.selectedTagIcon} />
              </button>
            ))}
          </div>
        )}
        <Input
          autoFocus
          size="small"
          allowClear
          className={cn.searchInput}
          prefix={<SearchOutlined />}
          placeholder="Поиск значения"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        {filter.optionsLoading ? (
          <div className={cn.popoverState}>
            <Loader size={18} />
          </div>
        ) : visibleOptions.length === 0 ? (
          <div className={cn.popoverState}>
            {sortedOptions.length === 0
              ? 'Нет доступных значений'
              : 'Ничего не найдено'}
          </div>
        ) : (
          <div className={cn.optionsGrid}>
            {visibleOptions.map((value) => {
              const isChecked = selected.has(value)
              const label = formatSlotValue(value)
              return (
                <button
                  type="button"
                  key={value}
                  title={label}
                  className={[cn.option, isChecked ? cn.optionChecked : '']
                    .filter(Boolean)
                    .join(' ')}
                  aria-pressed={isChecked}
                  onClick={() => toggleValue(slotIndex, value)}
                >
                  {label}
                </button>
              )
            })}
          </div>
        )}
        <div className={cn.popoverFooter}>
          <button
            type="button"
            className={cn.linkBtn}
            disabled={selected.size === 0}
            onClick={() =>
              onChange({ chosenOptions: withSlotValues(filter, slotIndex, []) })
            }
          >
            Очистить
          </button>
          {slotIndex === rowsCount && rowsCount > 1 && (
            <button
              type="button"
              className={cn.linkBtn}
              onClick={removeLastSlot}
            >
              Убрать присоединение
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={cn.slottedFilter}>
      <div className={cn.slotChips}>
        {Array.from({ length: rowsCount }, (_, i) => i + 1).map((slotIndex) => {
          const values = getSlotValues(filter, slotIndex).map(formatSlotValue)
          const summary = values.join(', ')

          return (
            <button
              type="button"
              key={slotIndex}
              ref={(element) => {
                chipRefs.current[slotIndex] = element
              }}
              className={[
                cn.slotChip,
                values.length ? cn.slotChipFilled : '',
                openSlot === slotIndex ? cn.slotChipActive : '',
              ]
                .filter(Boolean)
                .join(' ')}
              title={`${getSlotLabel(slotIndex)}: ${summary || 'любое значение'}`}
              aria-expanded={openSlot === slotIndex}
              onClick={() =>
                openPopover(openSlot === slotIndex ? null : slotIndex)
              }
            >
              <span className={cn.slotChipIndex}>{slotIndex}</span>
              <span className={cn.slotChipValue}>{values[0] ?? 'Любое'}</span>
              {values.length > 1 && (
                <span className={cn.slotChipMore}>+{values.length - 1}</span>
              )}
              <DownOutlined className={cn.slotChipArrow} />
            </button>
          )
        })}
        {rowsCount < MAX_SLOT_ROWS && (
          <Tooltip title="Добавить присоединение. Незаполненные присоединения не участвуют в фильтре">
            <button
              type="button"
              className={cn.addSlotBtn}
              onClick={() => {
                onChange({ slotRows: rowsCount + 1 })
                openPopover(rowsCount + 1)
              }}
            >
              <PlusOutlined />
            </button>
          </Tooltip>
        )}
      </div>
      {(totalSummary || showUniform) && (
        <div className={cn.summaryRow}>
          {totalSummary && (
            <span className={cn.summary} title={totalSummary}>
              <span className={cn.summaryLabel}>Итоговое значение:</span>
              {summaryGroups.map((group, index) => (
                <Fragment key={index}>
                  {index > 0 && <span className={cn.summaryTimes}>×</span>}
                  <span
                    className={[
                      cn.summarySlot,
                      group.length ? '' : cn.summarySlotEmpty,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {group.length === 0
                      ? '—'
                      : group.map((value, valueIndex) => (
                          <Fragment key={value}>
                            {valueIndex > 0 && (
                              <span className={cn.summaryOr}>/</span>
                            )}
                            {value}
                          </Fragment>
                        ))}
                  </span>
                </Fragment>
              ))}
            </span>
          )}
          {showUniform && (
            <UniformSlottedCheckbox
              checked={uniformChecked && isUniformEligible}
              disabled={!isUniformEligible}
              onChange={(checked) => onChange({ matchUniform: checked })}
            />
          )}
        </div>
      )}
      {openSlot !== null && (
        <SlotPopover anchor={anchorEl} onClose={closePopover}>
          {renderPopoverContent(openSlot)}
        </SlotPopover>
      )}
    </div>
  )
}

export default SlottedFilterInput
